import type { CSSObject, StyleProps } from '@chakra-ui/react'
import type { FontAwesomeIconProps } from '@fortawesome/react-fontawesome'
// @ts-expect-error
import type { components } from '@ias-video/api-types'

// UI Types
export const componentSizes = ['xs', 'sm', 'md', 'lg', 'xl'] as const
export type ComponentSize = (typeof componentSizes)[number]
export type ComponentStyleProps = Record<string, StyleProps | CSSObject | Omit<FontAwesomeIconProps, 'icon'>>
export type ComponentSizeStyleProps<TComponentParts extends ComponentStyleProps> = {
  [key in ComponentSize]?: TComponentParts
}

// API Related Types
export type UseQueryOmissions = 'queryKey' | 'queryFn'

// API Response Types
export type Customer = components['schemas']['VCCustomer']
export type Device = components['schemas']['VCDevice'] & {
  isBooked?: boolean
  isExpired?: boolean
  isOperator?: boolean
  isOwner?: boolean
  previewUrl?: string | null // Duplicate because original type specifies previewURL (unknown if both are used in different places)
}

export type Match = components['schemas']['Match']
export type MatchDay = components['schemas']['MatchDay']
export type MatchTeam = components['schemas']['MatchTeam']
export type Stream = components['schemas']['VCStream'] & {
  instanceNumber?: number
  streamGuid?: string
  configJson?: string
}
export type StreamCameraInputStatus = components['schemas']['STREAM_CAMERA_INPUT_STATUS']
export type StreamConfig = components['schemas']['VCStreamConfig']
export type StreamConfigDevice = components['schemas']['VCStreamConfigCamera']
export type StreamMatch = components['schemas']['Match'] & { instanceNumber?: number }
export type CustomerYouTubeAccount = {
  channelName: string
  channelId: string
  channelDescription: string
  channelUrl: string
  videoCount?: number | null
  viewCount?: number | null
  subscriberCount?: number | null
}

// Lookups
export const PrivacySettings = ['PUBLIC', 'PRIVATE', 'TAKEDOWN']
export const StreamCameraInputStatuses = ['NOT_RECEIVING', 'RECEIVING', 'ERROR', 'UNKNOWN', 'RECEIVING_BLANK']
export type MatchStatusType =
  | 'upcoming'
  | 'scheduled'
  | 'completed'
  | 'live'
  | 'preparing'
  | 'moved'
  | 'orphaned'
  | 'action_required'
export type YoutubeStatusType = 'CONNECTED' | 'NOT_CONNECTED' | 'INVALID_CONNECTION'

// External
export type CustomerYoutubeConfig = {
  AuthAccount: string
  TokenAccount: string
  AuthKey: string
  RefreshToken: string
  APIKey: string
  KeyType: string
}
export type CustomerProviderAuthConfig = {
  auth_code: string
}
export enum ProviderType {
  YOUTUBE = 1,
}

export type FreshchatUser = {
  id?: string
  firstName?: string
  lastName?: string
  email?: string
}

// GFX APIs
export type GraphicsProfile = {
  configGUID: string
  configID: number
  name: string
  isDefault: boolean
}
export type CustomerGraphicsConfig = {
  customerUuid: string
  name: string
  shortName: string
  logoURL: string
  logoFileName: string
  uploadLimitMb: number
  uploadLimitNum: number
  uploadLimitMbRaw: number
  uploadLimitNumRaw: number
}

export type MediaLibraryItem = {
  id: string
  fileName: string
  path: string
  fileSizeKB: number
  width: number
  height: number
  dateCreated: string
  dateUpdated: string
  approved: boolean
}

export type GraphicsRuleset = {
  id: number
  name: string
  default: boolean
  note?: string
  customerId?: string
  slotDefinitions: SlotDefinition[]
}

export type SlotDefinition = {
  id: number
  name: string
  locked: boolean
  maxInstances: number
  minWidth: number
  maxWidth: number
  minHeight: number
  maxHeight: number
  mediaLibraryItem: SlotMediaLibraryItem
}

type SlotMediaLibraryItem = {
  id: string
  filename: string
  url: string
  approved: boolean
  customerId: string
}

export type CustomerGraphic = {
  id: number
  uuid: string
  name: string
  customerId: string
  default: boolean
  imageSlots: CGImageSlot[]
  ruleset: CGRuleset
}

interface CGRuleset {
  id: number
  name: string
}

interface CGImageSlot {
  slotId: number
  instanceNumber: number
  locked: boolean
  mediaLibraryItem?: CGMediaLibraryItem
}

interface CGMediaLibraryItem {
  id: string
  filename: string
  url: string
  approved: boolean
}
